// appNav.mjs — 主导航的**唯一**事实来源（第五十六批修复）。
//
// 病灶：AppShell.jsx 里同时存在两份手工维护的路由清单——模块级 NAV 数组（五条，
// 只喂桌面端 tab），以及为移动端硬写的两个 MenuItem（审批/账号管理，各自带一份
// `account?.mayApprove` / `account?.isAdmin` 门条件）。谁后加了审批与管理，就只给
// 自己那条补了移动端出口，从没回头看那个更早的数组。于是 xs 断点下（桌面 Stack
// 被 `display:{xs:'none'}` 摘掉、又没有任何抽屉/底栏）五条成员入口全部无法抵达，
// 而唯二能点到的偏偏是投资方用户最用不上的两条。
//
// 所以修的不是"补个抽屉"，而是**把两份清单合成一份**：只要还留着两处可各自漂移的
// 列表，下一个人加入口时同样的洞会原样长回来。门条件写成 entry 上的 `when` 谓词，
// 全文件各出现一次；桌面与移动两个面都必须 map 同一个 navFor(account) 的结果，
// 该判据由 appNav.test.mjs 直接读 AppShell.jsx 源码钉死。
//
// 本模块保持纯函数、无 DOM、无 React 依赖（SB-042 约定：可判定逻辑入 .mjs 配单测）。

// ── 第一百零八批 C1：当事人类别（/api/me account 块的 `party`）─────────────────
// 三值："internal"（执行层的公司成员）/ "external"（合同相对方：外包、外部顾问）/
// "principal"（所有者出资方：资方）。判据**只认逐字 "internal"**——与后端
// `is_internal_party` 是同一条，两侧共用同一个字段而不是各自维护一张名单
//（名单是那个问题的镜像，而镜像会漂）。
//
// 三个刻意的取值行为：
//   · 账号缺失（AuthContext 首帧）⇒ false。这与"骨架不该少半截又长回来"的默认
//     取舍**方向相反**，是因为这一条挡的是泄漏：先亮后藏会让一个不该看见这页的人
//     真的点进去（且已经打过一次请求）；先藏后亮只是晚半拍出现，与 /approvals、
//     /admin 两条既有门控入口的行为一致。
//   · 未知的新值（服务端先上线了第四类）⇒ false。允许集比黑名单可靠。
//   · 字段整个缺席（旧版后端）⇒ false。宁可入口不出现，也不要它出现却点不动。
export function isInternalParty(account) {
  return account?.party === 'internal';
}

// ── 第一百零八批 R1（D3）：「这个账号能不能看 /threads 这一页」的唯一入口 ───────
// isInternalParty 回答的是一个**事实**（他站在哪一边），canSeeThreadsPage 回答的是一条
// **裁决**（这一页给不给他）。此刻两者同值，所以这不是为了加一层抽象——它有一条具体
// 的、付过账的理由：
//   对抗审查坐实，把 ThreadsPage.jsx 里那句早退整条删掉之后，前端全套测试**仍然全绿**。
//   外部账号从书签或深链进来会照常挂载看板、打两次注定 404 的请求、看到"加载失败"，
//   而那正是那一页的注释自称要防的事。根因是判定写在 `.jsx` 里，而 `.jsx` 不进单测
//   （SB-042 约定：可判定逻辑入 `.mjs` 配单测）。
// 于是判定搬到这里，成为一个**被测试钉住的入口**：谁把它改弱（放行 external/principal、
// 或把缺字段当成可见）当场红。页面那一侧由 appNav.test.mjs 读 ThreadsPage.jsx 源码钉住
// ——守卫必须还在，且必须排在挂载看板之前。
// 导航门与页面守卫共用这一个谓词：两处各写一遍就是两份会漂的口径，而这一位管的是
// 泄漏，漂开的一侧一定是"该藏的没藏"。
export function canSeeThreadsPage(account) {
  return isInternalParty(account);
}

// ── SB-197 S3b（第二百九十二批）：资产对账表四态记录页 ─────────────────────────
// 「这个账号该不该看见 /asset-progress」的唯一入口。后端的读闸是"能看台账页"（DocServe::
// viewable，台账 docKey）；今日能看台账页的岗位恰是执行层全员（`{group}/documents/**`），
// 合同相对方的视图不含 08-status，资方是管理员（恒 internal）。导航门取 isInternalParty
// 而不是再抄一份 glob——门条件挡的是"这页与你无关"，真正的闸在服务端（404）。
export function canSeeAssetProgress(account) {
  return isInternalParty(account);
}
// SB-201 S3（第三百零八批）：功能对账页与资产对账页同一道门——后端读闸同样是"能看那一页"
// （功能表 docKey 在理想书 08-status，执行层全员可见、承包方视图不含）。
export function canSeeFeatureProgress(account) {
  return canSeeAssetProgress(account);
}

// ── SB-117（第一百一十三批）：到期动作 ────────────────────────────────────────
// 「这个账号该不该看见到期动作」的唯一入口。与 canSeeThreadsPage 同一条姿态：
// 导航门与**壳上那条横幅**必须问同一个谓词——两处各写一遍 `account?.isAdmin`，
// 就是两份会漂的口径，而本文件的立项理由恰恰是"门条件各自漂开"那个洞。
export function isAdminAccount(account) {
  return Boolean(account?.isAdmin);
}

// 到期动作那一页给谁看。今天它与 isAdminAccount 同值，所以这不是多一层抽象——
// 理由与 canSeeThreadsPage 逐字同源：`isAdminAccount` 回答的是一个**事实**
//（他是不是管理员），`canSeeDueActions` 回答的是一条**裁决**（这一页给不给他）。
// 哪天到期动作要给非管理员的执行主体看（"执行主体"本就是一个自由文本字段），
// 改的是这里一处，而不是满仓找 `isAdmin`。
export function canSeeDueActions(account) {
  return isAdminAccount(account);
}

// 到期动作那一页的路径。**导出它是为了让 AppShell 不必硬写路由字面量**：
// appNav.test.mjs 钉着「AppShell.jsx 只剩一个路由字面量：登出跳转 /login」，
// 而壳上那条横幅需要一颗按钮通向这里。路径的事实来源只此一处。
export const DUE_ACTIONS_PATH = '/due-actions';

// 条目形状：{ to, key, when? }
//   to   —— 路由字面量，同时用作 React key（全表唯一，单测保证）
//   key  —— i18n 键，文案不在这里落地（母语原则 4：文案随界面语言）
//   when —— 可见性谓词，(account) => boolean；缺省即所有登录账号可见
// 顺序即两个面的呈现顺序：先五条成员入口，受门控的两条排在末尾——移动抽屉是纵向
// 列表，把少数人才有的入口压在下面，多数人的手指路径最短。
export const NAV_ENTRIES = [
  { to: '/docs', key: 'shell.nav.docs' },
  { to: '/submit', key: 'shell.nav.submit' },
  { to: '/my-tickets', key: 'shell.nav.myTickets' },
  { to: '/work-items', key: 'shell.nav.workItems' },
  // 第一百零二批（SB-106）：项目汇报读面。紧贴工作项——两者是同一类东西的两面
  // （工作项＝我们要做什么，汇报＝对方做到哪了），SB-106 也把落点写成"工作项页旁"。
  // 横向预算：sm 起这一行早已在自己的 overflowX 容器里滚（第五十九批把这条封住时
  // 就是按"再加入口只增加滚动长度、不增加 body 溢出"设计的），所以净增量落在滚动
  // 长度上而不是溢出上；xs 是纵向抽屉，加一条净增 0。标签取全栏最短的两个字
  //（"汇报"/"Reports"），仍在 MUI 文本 Button 的 64px 下限内，即增量就是那 64px。
  { to: '/reports', key: 'shell.nav.reports' },
  // 第一百零四批 B：组间通信。读面对**执行层**全员透明（不按当前组过滤，全量可读；
  // 写面的 canLiaison 门在页面内管按钮，不在导航上）。紧贴 /reports：两者同属
  // "项目侧在说什么"的读面。横向预算沿用第五十九批的口径：sm 起 tab 栏在自己的
  // overflowX 容器里滚，净增量落在滚动长度上；xs 是纵向抽屉，净增 0。
  //
  // 第一百零八批 C1：**它从"无门条件"那一段移进了门控段**。此前它排在成员入口里，
  // 因为"这页对每个成员都有关"——那句话现在只对执行层成立（后端对其余人整族 404，
  // 判据与理由见 routes_threads.cpp 头注）。导航门挡的是"这页与你无关"，而对一个
  // 点进去只会看到拒绝的人，这页确实与他无关。
  // 门条件用 canSeeThreadsPage（＝isInternalParty，见上）：导航门与页面守卫必须问
  // 同一个人，否则"入口藏了但页面还渲染"或反过来，两种漂法各错一半。
  { to: '/threads', key: 'shell.nav.threads', when: canSeeThreadsPage },
  // SB-197 S3b（第二百九十二批）：资产对账表四态记录。紧贴 /threads——同属"项目侧在
  // 说什么／做到哪了"的执行层读面；写面（记态）的角色由服务端按岗位派生，页内按钮显隐，
  // 不是导航门。横向预算沿用第五十九批口径：sm 起 tab 栏在自己的 overflowX 容器里滚。
  { to: '/asset-progress', key: 'shell.nav.assetProgress', when: canSeeAssetProgress },
  // SB-201 S3（第三百零八批）：功能对账。紧贴 /asset-progress——两张对账表是同一套机制的两面
  //（素材就位 / 功能妥善），资方简报的两个分子也正是它们。
  { to: '/feature-progress', key: 'shell.nav.featureProgress', when: canSeeFeatureProgress },
  // 第一百零九批 D：一对一站内信。紧贴 /threads，因为两者是同一族的两个粒度
  //（组对组·全员可读 / 一对一·可撤回），而此前这一条由需求方人肉代传。
  // **无门条件**，与 /threads 刻意不同：组间通信的透明口径只对执行层成立（第一百零八
  // 批 C1），而一封信是**人对人**的——外包与外部顾问同样需要一条能给具体某个人写字
  // 的通道，后端的闸也只有 require_session（routes_messages.cpp：不要求 group_liaison,
  // "这是人对人的信，不是代表组说话"）。在导航上加一道后端没有的门，就是把一条
  // 用得着的路对着最需要它的人关上。
  { to: '/messages', key: 'shell.nav.messages' },
  { to: '/work-hours', key: 'shell.nav.workHours' },
  // 第一百零九批 D：密件取件面。同样无门条件——它的可见集完全是**数据**边界
  //（收件人是我 ∪ 签发方是我，由后端逐行判，非相关者一律看不见连"有这么一件"都不知道），
  // 而不是一把要在导航上再表达一遍的锁。在这里加门等于把同一条规则放到两处，
  // 而两处迟早会漂开——漂开的那一侧是"收到 key 的人找不到取件的地方"。
  //
  // 位置：成员段的末尾、/help 之前。低频但要紧的入口（一个新接入方一辈子可能只用
  // 一次，而那一次是他能不能开始工作的前提），与"我卡住了"落在同一段手指路径上。
  { to: '/secrets', key: 'shell.nav.secrets' },
  // 使用手册。排在成员入口的**末位**、受门控的两条之前，有三个理由：
  //   1. 它对所有登录账号可见，属于无门条件那一段，不能插进门控段里；
  //   2. 移动抽屉是纵向列表，"我卡住了"是低频但要找得到的入口，压在成员段最后一行
  //      正好是手指扫到底停下的位置；
  //   3. 桌面 tab 栏横向预算最紧（见 AppShell 里的算式），末位意味着它是最后才被挤到的
  //      那一格，前面四条日常入口的可读性不受影响。
  // 标签必须是全栏最短的（"帮助"/"Help"，两者都落在 MUI Button 的 64px 下限内），
  // 所以这一条在 sm+ 断点上的净增量就是那 64px 下限本身，不多一分。
  { to: '/help', key: 'shell.nav.help' },
  // 门条件在整个前端只此一处。以前它在 AppShell.jsx 里各写两遍（桌面按钮 + 移动
  // 菜单项），改一处漏一处就是上面那个洞的直接成因。
  { to: '/approvals', key: 'shell.nav.approvals', when: (a) => Boolean(a?.mayApprove) },
  // 门条件走具名谓词而不是内联表达式：本文件的判据钉着「`isAdmin` 全文件只出现
  // 一次」，而第一百一十三批加第二条管理员入口时，那条判据当场红——它要的正是
  // 「同一个事实只读一处」，而不是「只准有一条管理员入口」。
  { to: '/admin', key: 'shell.nav.admin', when: isAdminAccount },
  // SB-117（第一百一十三批）：到期动作。排在 /admin 之后——它是运营面而不是成员面，
  // 而真正的抵达路径是**壳上那条横幅**（验收判准是「他不去找也会撞见」，
  // 任何导航条目都满足不了它）。导航条目是那条横幅的兜底：横幅只在有过期或
  // 今天到期时出声，而人有时想主动去看一眼还有什么在排队。
  { to: '/due-actions', key: 'shell.nav.dueActions', when: canSeeDueActions },
];

// 该账号可见的导航条目。纯函数：不改 NAV_ENTRIES、不读模块外状态，
// 返回的元素就是 NAV_ENTRIES 里的同一批对象（调用侧只读）。
// account 允许为 null/undefined（AuthContext 在首帧尚未拿到账号），此时无门条件的
// 条目照常返回——渲染出来的骨架不该在一瞬间少半截又长回来。
export function navFor(account) {
  return NAV_ENTRIES.filter((e) => (typeof e.when === 'function' ? Boolean(e.when(account)) : true));
}

// 当前路由是否命中某条入口。子路由算命中（/docs/xxx 属于"文档"，/admin/git 属于
// "账号管理"），但只按**路径段**边界匹配：直接用 startsWith(to) 会让 /work-items
// 把 /work-items-archive 之类的兄弟路由也点亮。
export function isActiveRoute(pathname, to) {
  const p = String(pathname || '');
  return p === to || p.startsWith(`${to}/`);
}
